import { useEffect, useRef, useState } from "react";
import { Annotation } from "../interfaces/annotation";
import { useAppSelector } from "../../app";
import annotationToScrollToSelector from "../selectors/annotationToScrollToSelector";
import { shallowEqual } from "react-redux";
import useScrollToElement, {
  resetLastScrolledElement,
} from "./useScrollToElement";

/**
 * Scrolls to a specific page in the PDF viewer, then waits for an element to appear.
 * Returns a promise that resolves when the element is found or times out.
 */
const scrollToPageThenWaitForElement = (
  pageNumber: number,
  elementId: string,
  containerId: string = "annotator-pages-container",
  maxRetries: number = 20,
  retryDelay: number = 50,
): Promise<HTMLElement | null> => {
  return new Promise((resolve) => {
    const container = document.getElementById(containerId);
    if (!container) {
      resolve(null);
      return;
    }

    // First check if element already exists
    const existingElement = document.getElementById(elementId);
    if (existingElement) {
      resolve(existingElement);
      return;
    }

    // Find and scroll to the page (pages are 1-indexed, array is 0-indexed)
    const pages = container.querySelectorAll(".page");
    const targetPage = pages[pageNumber - 1] as HTMLElement;

    if (targetPage) {
      // Scroll page into view first (instant scroll to avoid animation conflicts)
      targetPage.scrollIntoView({ behavior: "auto", block: "center" });
    }

    // Wait for the element to appear in the DOM
    let retries = 0;
    const checkForElement = () => {
      const element = document.getElementById(elementId);
      if (element) {
        resolve(element);
        return;
      }

      retries++;
      if (retries < maxRetries) {
        setTimeout(checkForElement, retryDelay);
      } else {
        resolve(null);
      }
    };

    // Start checking after a brief delay to allow page to render
    setTimeout(checkForElement, retryDelay);
  });
};

const useScrollToAnnotation = (annotation: Annotation) => {
  const scrollToAnnotation = useAppSelector(
    annotationToScrollToSelector,
    shallowEqual,
  );

  const { scrollToElement } = useScrollToElement();

  // Use state instead of ref to trigger re-renders when pulsate status changes
  const [scrollClassName, setScrollClassName] = useState("");

  // Track the last target we processed to avoid duplicate scrolls
  const processedTargetRef = useRef<string | null>(null);
  const isScrollingRef = useRef<boolean>(false);

  // Check if this annotation is the current scroll target
  const isTargetAnnotation =
    scrollToAnnotation &&
    scrollToAnnotation.id === annotation.entity.id &&
    scrollToAnnotation.index === annotation.index;

  // Create target identifier
  const currentTargetId = scrollToAnnotation
    ? `${scrollToAnnotation.id}-${scrollToAnnotation.index}`
    : null;

  // Clear highlight when this annotation is no longer the target.
  // Done while rendering rather than from an effect: same guard, same resulting
  // value, it just drops the extra paint that still showed the highlight after
  // this annotation had already stopped being the scroll target. The scroll
  // effect below only ever sets the class while `isTargetAnnotation` is true, so
  // it cannot race with this.
  if (!isTargetAnnotation && scrollClassName) {
    setScrollClassName("");
  }

  // Main scroll effect - only runs when target changes
  useEffect(() => {
    // Skip if not the target or no target
    if (!isTargetAnnotation || !currentTargetId) {
      return;
    }

    // Skip if we already processed this exact target
    if (processedTargetRef.current === currentTargetId) {
      return;
    }

    // Skip if already scrolling
    if (isScrollingRef.current) {
      return;
    }

    // Mark as processing
    processedTargetRef.current = currentTargetId;
    isScrollingRef.current = true;

    const elementId = `${annotation.id}-${annotation.index}-0`;

    // Function to apply highlight
    const applyHighlight = () => {
      setScrollClassName("");
      requestAnimationFrame(() => {
        setScrollClassName("pulsate-fwd");
      });
    };

    // Function to perform scroll with retry for cross-page navigation
    const doScroll = async () => {
      try {
        // First try direct scroll
        const scrolled = scrollToElement(elementId, {
          behavior: "smooth",
          block: "center",
        });

        if (scrolled) {
          applyHighlight();
          return;
        }

        // Check if element exists but scroll was blocked by lock
        const existingElement = document.getElementById(elementId);
        if (existingElement) {
          // Element exists, scroll was just blocked - still apply highlight
          applyHighlight();
          return;
        }

        // Element not found - scroll to page first, then wait for element
        const element = await scrollToPageThenWaitForElement(
          annotation.page,
          elementId,
        );

        if (element) {
          // Small delay to let the page settle
          await new Promise((resolve) => setTimeout(resolve, 50));

          scrollToElement(elementId, {
            behavior: "smooth",
            block: "center",
          });
          applyHighlight();
        }
      } finally {
        isScrollingRef.current = false;
      }
    };

    doScroll();
  }, [
    currentTargetId,
    isTargetAnnotation,
    annotation.id,
    annotation.index,
    annotation.page,
    scrollToElement,
  ]);

  // Reset processed target when global target changes
  useEffect(() => {
    // When target changes to something else, reset our processed marker
    if (
      processedTargetRef.current &&
      processedTargetRef.current !== currentTargetId
    ) {
      processedTargetRef.current = null;
      resetLastScrolledElement();
    }
  }, [currentTargetId]);

  return scrollClassName;
};

export default useScrollToAnnotation;
